// Imports => Vendor
import axios from 'axios';

// Imports => MOBX
import { makeObservable, observable, computed, action } from 'mobx';

// Imports => Constants
import { KEYS } from '@opp/ui-kit';

// Imports => Models
import { TransactionModel } from '@models/transaction.model';

// Imports => Utilities
import {
  AcSanitize,
  AcSaveState,
  AcRemoveState,
  AcIsSet,
  AcIsNull,
  AcIsEmptyString,
  AcFormatErrorMessage,
  AcFormatErrorCode,
  AcIsUndefined,
  AcFormatRequestParameters,
} from '@opp/ui-kit';
import { AcAutoLoad, AcAutoSave } from '@utils';

const _default = {
  options: {},
  search_results: null,
  chart_data: null,
  totals: null,
};

let app = {};

export class GlobalStore {
  constructor(store) {
    makeObservable(this);

    AcAutoLoad(this, KEYS.SEARCH_RESULTS);
    AcAutoLoad(this, KEYS.CHART_DATA);
    AcAutoLoad(this, KEYS.TOTALS);
    AcAutoSave(this);

    app.store = store;
  }

    @observable
      search_results = _default.search_results;

    @observable
      chart_data = _default.chart_data;

    @observable
      totals = _default.totals;

    @computed
    get current_search_results() {
      return this.search_results;
    }

    @computed
    get current_chart_data() {
      return this.chart_data;
    }

    @computed
    get current_totals() {
      return this.totals;
    }

    @observable
      loading = {
        status: false,
        message: null,
      };

    @computed
    get is_loading() {
      return this.loading.status;
    }

    @action
      setLoading = (state, message) => {
        this.loading = {
          status: state || false,
          message,
        };
      };

    @observable
      busy = {
        status: false,
        message: null,
      };

    @computed
    get is_busy() {
      return this.busy.status;
    }

    @action
      setBusy = (state, message) => {
        this.busy = {
          status: state || false,
          message,
        };
      };

    @observable
      order_by = {
        field: null,
        direction: KEYS.ASCENDING,
      };

    @observable
      page = 1;

    @observable
      perpage = 10;

    @observable
      query = null;

    @action
      setQuery = (input) => {
        const _input = AcSanitize(input);
        if (this.current_query !== _input) this.query = _input;
      };

    @action
      setPageNumber = (num) => {
        if (this.page !== num) this.page = num;
      };

    @action
      setPerPage = (num) => {
        if (this.perpage !== num) this.perpage = num;
      };

    @action
      setOrderBy = (field, direction = null) => {
        let order_by = this.order_by;

        if (AcIsSet(direction)) {
          order_by.direction = direction;
        } else if (order_by.field === field) {
          order_by.direction =
                order_by.direction === KEYS.ASCENDING
                  ? KEYS.DESCENDING
                  : KEYS.ASCENDING;
        } else order_by.direction = KEYS.ASCENDING;

        order_by.field = field;

        this.order_by = order_by;
        this.setPageNumber(1);
      };

    @computed
    get current_page() {
      return this.page;
    }

    @computed
    get current_per_page() {
      return this.perpage;
    }

    @computed
    get current_order_by() {
      return this.order_by;
    }

    @computed
    get current_query() {
      return this.query;
    }

    @action
      resetParams = () => {
        this.page = 1;
        this.query = null;
        this.perpage = 10;
        this.order_by = { field: null, direction: KEYS.ASCENDING };
      };

    @action
      search = (t, query = '', options = {}) => {
        this.setLoading(true);

        if (AcIsSet(query)) {
          this.setQuery(query);
        }

        const params = AcFormatRequestParameters(this, { ...options, uid: query });

        return app.store.api.global
          .search(params)
          .then(async (response) => {
            const collection = response;
            const len = collection.length;
            let n = 0;
            let result = [];

            for (n; n < len; n++) {
              const item = collection[n];
              const formatted = new TransactionModel(item);
              result.push(formatted);
            }

            await this.set(KEYS.SEARCH_RESULTS, result);

            this.setLoading(false);

            return response;
          })
          .catch((error) => {
            if (!axios.isCancel(error)) {
              app.store.toasters.add({
                variant: 'error',
                title: t('global.no-search-result', {query: query, ns: 'store'}),
                description: AcFormatErrorMessage(error),
                code: AcFormatErrorCode(error),
              });
            }

            this.setLoading(false);

            if (!axios.isCancel(error)) throw error;
          });
      };

    @action
      get_chart_data = (t, options = {}) => {
        this.setLoading(true);

        const params = AcFormatRequestParameters(this, options, true);

        return app.store.api.global
          .chart(params)
          .then(async (response) => {
            await this.set(KEYS.CHART_DATA, response, true);

            this.setLoading(false);

            return response;
          })
          .catch((error) => {
            if (!axios.isCancel(error)) {
              app.store.toasters.add({
                variant: 'error',
                title: t('global.failed-to-retrieve-incoming-outgoing', {ns: 'store'}),
                description: AcFormatErrorMessage(error),
                code: AcFormatErrorCode(error),
              });
            }

            this.setLoading(false);

            if (!axios.isCancel(error)) throw error;
          });
      };

    @action
      get_totals = (t) => {
        this.setLoading(true);

        return app.store.api.global
          .totals()
          .then(async (response) => {
            await this.set(KEYS.TOTALS, response, true);

            this.setLoading(false);

            return response;
          })
          .catch((error) => {
            if (!axios.isCancel(error)) {
              app.store.toasters.add({
                variant: 'error',
                title: t('global.failed-to-retrieve-statistics', {ns: 'store'}),
                description: AcFormatErrorMessage(error),
                code: AcFormatErrorCode(error),
              });
            }

            this.setLoading(false);

            if (!axios.isCancel(error)) throw error;
          });
      };

    @action
      set = (target, value, save = false) => {
        if (!AcIsSet(target)) return;
        if (AcIsUndefined(this[target])) return;
        if (AcIsUndefined(value)) return;

        return new Promise((resolve) => {
          this[target] = value;
          if (save) AcSaveState(target, value);
          resolve();
        });
      };

    @action
      setState = (target, property, value, save) => {
        if (!AcIsSet(target)) return;
        if (AcIsUndefined(this[target])) return;
        if (!AcIsSet(property)) return;
        if (AcIsUndefined(value)) return;

        this[target][property] = value;
        if (save) AcSaveState(target, value);
      };

    @action
      reset = (target, save = false) => {
        if (!AcIsSet(target)) return;
        if (AcIsUndefined(this[target])) return;

        return new Promise((resolve) => {
          this[target] = _default[target];
          if (save && AcIsNull(_default[target])) {
            AcRemoveState(target);
          } else if (save) {
            AcSaveState(target, _default[target]);
          }

          resolve();
        });
      };
}

export default GlobalStore;
